import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Instagram, ExternalLink, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { ADDRESS, LINKS, IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0B1520]/60 px-4 py-3 text-sm text-[#F7F5F0] placeholder-[#5C7285] focus:outline-none focus:border-[#E5AC3C] focus:ring-1 focus:ring-[#E5AC3C] transition-colors";

const SUBJECTS = ["General enquiry", "Membership — Senior", "Junior cricket & All Stars", "Girls' cricket", "Women's team 2027", "Sponsorship", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, kind: "contact" });
      toast.success(res.data.message);
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact Us — Whitley Bay Cricket Club"
        description="Get in touch with Whitley Bay Cricket Club at West Park, Hotspur Avenue, Whitley Bay NE25 8RP — membership, juniors, girls' and women's cricket."
      />
      <PageHeader
        eyebrow="We'd love to hear from you"
        title="Contact"
        accent="the club."
        description="New players, parents, sponsors and social members — drop us a line and we'll point you in the right direction."
        image={IMAGES.lighthouse}
        testId="contact-header"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <Reveal>
          <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#122030] p-8 sm:p-10" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required minLength={2} className={inputCls} placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              <input required type="email" className={inputCls} placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <select className={`${inputCls} mt-4`} value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-select">
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <textarea required minLength={5} rows={5} className={`${inputCls} mt-4`} placeholder="Your message" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-6 w-full sm:w-auto bg-[#E5AC3C] hover:bg-[#F3C35B] disabled:opacity-60 text-[#0B1520] font-bold px-8 py-3.5 rounded-full text-sm transition-colors"
            >
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-white/10 bg-[#122030] p-8" data-testid="contact-details-card">
              <h2 className="font-head text-lg font-bold inline-flex items-center gap-2">
                <MapPin size={18} className="text-[#E5AC3C]" /> Find us
              </h2>
              <p className="mt-3 text-sm text-[#9BB0C1] leading-relaxed">{ADDRESS}</p>
              <div className="mt-5 rounded-xl overflow-hidden border border-white/10">
                <iframe
                  title="Map to West Park, Whitley Bay"
                  src="https://www.google.com/maps?q=West+Park,+Hotspur+Avenue,+Whitley+Bay+NE25+8RP&output=embed"
                  className="w-full h-56 grayscale-[30%] contrast-[1.05]"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="rounded-3xl border border-white/10 bg-[#122030] p-8" data-testid="contact-social-card">
              <h2 className="font-head text-lg font-bold">Follow & fixtures</h2>
              <div className="mt-4 flex flex-col gap-3 text-sm">
                <a href={LINKS.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors" data-testid="contact-facebook-link">
                  <Facebook size={16} /> Facebook — club news & photos
                </a>
                <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors" data-testid="contact-instagram-link">
                  <Instagram size={16} /> Instagram
                </a>
                <a href={LINKS.playCricket} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#9BB0C1] hover:text-[#E5AC3C] transition-colors" data-testid="contact-playcricket-link">
                  <ExternalLink size={16} /> Play-Cricket — fixtures, results & selection
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
